import AsyncSelectComponent from '@components/inputs/asyncSelect/AsyncSelect.component';
import SelectComponent, { SelectItem } from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import { Sizes } from '@constants/sizes.constants';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { IconButton, Stack, Typography } from '@mui/material';
import {
    AuditOperationType,
    AuditOperationTypeEnum,
    AuditOwnedBy,
    AuditOwnedByEnum,
    CreateAuditOperation
} from '@store/audits/audits-operation/audits-operation.model';
import { containersService } from '@store/settings/containers';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { map } from 'rxjs';

interface AuditContainerProps {
    isPreco?: boolean;
    auditOperation?: CreateAuditOperation;
    updateAuditOperation: <T extends keyof CreateAuditOperation>(
        field: T,
        value: CreateAuditOperation[T]
    ) => void;
    errors: any;
}

const ContainerInformations = (props: AuditContainerProps) => {

    const {
        auditOperation,
        updateAuditOperation,
        errors
    } = props;

    const [containerKind, setContainerKind] = useState<SelectItem>({ label: auditOperation?.container?.name ?? '', value: auditOperation?.containerId ?? '' });


    useEffect(() => {
        if (auditOperation?.containerId) {
            setContainerKind({ label: auditOperation?.container?.name ?? '', value: auditOperation?.containerId });
        }
        updateAuditOperation('whereIs', auditOperation?.description);
        if (auditOperation?.by === AuditOwnedByEnum.CLIENT) {
            handleIsClient();
        } else if (auditOperation?.by === AuditOwnedByEnum.SUPPLIER) {
            handleIsSupplier();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auditOperation?.container, auditOperation?.containerId, auditOperation?.by, auditOperation?.description, updateAuditOperation]);

    function handleIsClient() {
        updateAuditOperation('by', AuditOwnedByEnum.CLIENT);
    }

    function handleIsSupplier() {
        updateAuditOperation('by', AuditOwnedByEnum.SUPPLIER);
    }

    function handlecontainerKind(container: any) {
        updateAuditOperation('containerId', container?.value);
        setContainerKind(container);
    }

    function handleTypeChange(value: any) {
        updateAuditOperation('type', value);
        updateAuditOperation('containerId', undefined);
        updateAuditOperation('containerCustom', undefined);
        updateAuditOperation('wasteId', undefined);
        updateAuditOperation('wasteCustom', undefined);
    }

    const { t } = useTranslation();

    return (
        <>
            <Stack direction="row" mt="3px" alignItems="center" spacing={0.5}>
                <Typography fontSize={20} fontWeight="700">
                    {t('audits.audit.container.informations')}
                </Typography>
            </Stack>
            <Stack py="24px" pr="10px" spacing={1.5}>
                <SelectComponent
                    color="primary"
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('audits.audit.type')}
                    placeholder={t('global.select')}
                    items={AuditOperationType.selectItems()}
                    handleChange={(value) => handleTypeChange(value)}
                    value={auditOperation?.type ?? ''}
                />

                {auditOperation?.type === AuditOperationTypeEnum.CLASSIC ?
                    <AsyncSelectComponent
                        labelWidth={Sizes.drawerInputLabelWidth}
                        label={t('audits.audit.container.type')}
                        placeholder={t('global.select')}
                        getOptions={() => containersService.getContainers().pipe(
                            map((options) => options.data.map((o) => ({ label: o.name || '-', value: o.id }))))}
                        value={containerKind}
                        handleChange={handlecontainerKind}
                        error={errors.container}
                        required={true}
                    />
                    :
                    <TextFieldComponent
                        color="primary"
                        labelWidth={Sizes.drawerInputLabelWidth}
                        placeholder={t('audits.audit.container.type')}
                        label={t('audits.audit.container.type')}
                        handleChange={(value) => updateAuditOperation('containerCustom', value)}
                        value={auditOperation?.containerCustom ?? ''}
                        error={errors.container}
                        required={true}
                    />
                }



                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography color={auditOperation?.quantity ? '#848F9D' : '#E6433C'} fontSize={14} fontWeight="100">
                        {t('audits.audit.container.number') + '*'}
                    </Typography>
                    <Stack direction='row' spacing={1.5} alignItems='center'>
                        <IconButton onClick={() => updateAuditOperation('quantity', auditOperation?.quantity ? auditOperation.quantity - 1 : 0)}>
                            <RemoveIcon sx={{ backgroundColor: '#192133', color: 'white', width: '25px', borderRadius: '4px' }} />
                        </IconButton>
                        <Typography fontSize={14} fontWeight="400">
                            {auditOperation?.quantity ?? '0'}
                        </Typography>
                        <IconButton onClick={() => updateAuditOperation('quantity', auditOperation?.quantity ? auditOperation.quantity + 1 : 1)}>
                            <AddIcon sx={{ backgroundColor: '#192133', color: 'white', width: '25px', borderRadius: '4px' }} />
                        </IconButton>
                    </Stack>
                </Stack>
                <SelectComponent
                    color="primary"
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('audits.audit.container.own')}
                    placeholder={t('global.select')}
                    items={AuditOwnedBy.selectItems()}
                    handleChange={(value) => updateAuditOperation('by', value)}
                    value={auditOperation?.by}
                    error={errors.by}
                    required={true}
                />
                <TextFieldComponent
                    multiline
                    color="primary"
                    labelWidth={Sizes.drawerInputLabelWidth}
                    placeholder={t('audits.audit.container.where')}
                    rows={10}
                    handleChange={(value) => updateAuditOperation('whereIs', value)}
                    value={auditOperation?.whereIs ?? ''}
                    error={errors.whereIs}
                />
            </Stack>
        </>
    );
};

export default ContainerInformations;